import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { Bookmark, X } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import {
	type AudienceFilter,
	audienceKeys,
	createSavedFilter,
	deleteSavedFilter,
	listSavedFilters,
	type SavedFilter,
} from "@/entities/audience"
import { Button } from "@/shared/ui/button"
import { Input } from "@/shared/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover"

const NAME_LIMIT = 60

/** Выборки, которые сотрудники сохранили для этого бота: общие на всех. */
export function useSavedFilters(botId: string) {
	return useQuery({
		queryKey: audienceKeys.saved(botId),
		queryFn: () => listSavedFilters(botId),
		staleTime: 60_000,
	})
}

/** Сохранённые выборки плашками: клик применяет, крестик удаляет. */
export function SavedFilterChips({
	botId,
	onApply,
}: {
	botId: string
	onApply: (filter: AudienceFilter) => void
}) {
	const queryClient = useQueryClient()
	const saved = useSavedFilters(botId)
	const remove = useMutation({
		mutationFn: (id: string) => deleteSavedFilter(botId, id),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: audienceKeys.saved(botId) }),
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось удалить выборку")),
	})
	return (
		<>
			{saved.data?.map((item: SavedFilter) => (
				<span
					key={item.id}
					className="group inline-flex h-8 items-center rounded-full bg-secondary/60 text-sm"
				>
					<button
						type="button"
						className="flex h-full items-center gap-1.5 pr-1 pl-3 hover:text-foreground"
						title="Применить сохранённую выборку"
						onClick={() => onApply(item.filter)}
					>
						<Bookmark className="size-3.5 text-primary" />
						{item.name}
					</button>
					<button
						type="button"
						aria-label={`Удалить выборку ${item.name}`}
						title="Удалить выборку для всех"
						className="grid size-7 place-items-center rounded-full text-muted-foreground opacity-60 hover:text-foreground group-hover:opacity-100"
						onClick={() => remove.mutate(item.id)}
					>
						<X className="size-3.5" />
					</button>
				</span>
			))}
		</>
	)
}

/** «Сохранить выборку»: текущие условия под понятным именем, видны всем сотрудникам. */
export function SaveFilterButton({
	botId,
	filter,
	suggestedName,
}: {
	botId: string
	filter: AudienceFilter
	suggestedName: string
}) {
	const queryClient = useQueryClient()
	const [open, setOpen] = useState(false)
	const [name, setName] = useState("")
	const save = useMutation({
		mutationFn: () => createSavedFilter(botId, name.trim(), filter),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: audienceKeys.saved(botId) })
			setOpen(false)
			toast.success("Выборка сохранена, она появится рядом с быстрыми фильтрами")
		},
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось сохранить выборку")),
	})
	return (
		<Popover
			open={open}
			onOpenChange={(next) => {
				setOpen(next)
				if (next) setName(suggestedName.slice(0, NAME_LIMIT))
			}}
		>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="ghost"
					className="h-8 rounded-full px-3 text-sm text-muted-foreground"
				>
					<Bookmark />
					Сохранить выборку
				</Button>
			</PopoverTrigger>
			<PopoverContent align="start" className="w-72 space-y-2 p-3">
				<p className="text-xs text-muted-foreground">
					Выборка будет видна всем сотрудникам в списке пользователей этого бота.
				</p>
				<form
					className="flex gap-2"
					onSubmit={(event) => {
						event.preventDefault()
						if (name.trim()) save.mutate()
					}}
				>
					<Input
						autoFocus
						aria-label="Название выборки"
						maxLength={NAME_LIMIT}
						value={name}
						onChange={(event) => setName(event.target.value)}
					/>
					<Button type="submit" disabled={!name.trim() || save.isPending}>
						Сохранить
					</Button>
				</form>
			</PopoverContent>
		</Popover>
	)
}
